import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2Icon, CheckIcon, ClockIcon, ExternalLinkIcon, Loader2Icon, MailIcon, MoreHorizontalIcon, PhoneIcon, SparklesIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { upNext } from '../../data/perfectDay';
interface UpNextPanelProps {
  onViewRecord: (recordSetId: string) => void;
  onToast: (message: string) => void;
}
type DialogKind = 'call' | 'email' | 'defer' | null;
const deferOptions = [{
  id: '1h',
  label: 'In 1 hour',
  detail: 'Returns at 11:00 AM'
}, {
  id: 'pm',
  label: 'This afternoon',
  detail: 'Returns at 1:30 PM'
}, {
  id: 'tomorrow',
  label: 'Tomorrow morning',
  detail: 'Returns at 8:00 AM'
}];
export function UpNextPanel({
  onViewRecord,
  onToast
}: UpNextPanelProps) {
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [deferredLabel, setDeferredLabel] = useState<string | null>(null);
  const [deferChoice, setDeferChoice] = useState(deferOptions[0].id);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  const runAction = (message: string, onDone?: () => void) => {
    setSaving(true);
    window.setTimeout(() => {
      setSaving(false);
      setDialog(null);
      onDone?.();
      onToast(message);
    }, 800);
  };
  const handleComplete = () => {
    setMenuOpen(false);
    setCompleted(true);
    setDeferredLabel(null);
    onToast('Activity logged — Morgan Taylor marked complete.');
  };
  return <section aria-labelledby="up-next-heading" className="overflow-hidden rounded-2xl border border-brand-200 bg-white shadow-card" data-id="element-98" data-mp="1gcg0rba59ivg">
      <div className="flex items-center justify-between gap-3 bg-navy-800 px-5 py-3" data-id="element-99" data-mp="1p51ico21hjip">
        <h2 id="up-next-heading" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white" data-id="element-100" data-mp="1u3uwmip5hejx">
          <SparklesIcon className="h-4 w-4 text-teal-300" aria-hidden="true" data-id="element-101" data-mp="1z14l9w4ptm6p" />
          Up Next
        </h2>
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-teal-200" data-id="element-102" data-mp="242k73qepvwq5">
          {upNext.impactLabel}
        </span>
      </div>

      <div className="p-5" data-id="element-103" data-mp="9pc6fi428qwl">
        {completed ? <div className="rounded-xl border border-success-200 bg-success-50 p-5 text-center" data-id="element-104" data-mp="dotb49bnxk0p">
            <CheckCircle2Icon className="mx-auto h-7 w-7 text-success-600" aria-hidden="true" data-id="element-105" data-mp="65k42mqmpxet" />
            <h3 className="mt-2 text-base font-semibold text-success-700" data-id="element-106" data-mp="1ebt3m9ni3zj">
              Nice work — first contact made
            </h3>
            <p className="mt-1 text-sm text-success-700/80" data-id="element-107" data-mp="3sj22q1nn81x8">
              {upNext.contactName} is contacted and logged. Your next
              recommendation refreshes in a few minutes.
            </p>
            <button type="button" onClick={() => setCompleted(false)} className="mt-4 inline-flex h-11 items-center justify-center rounded-lg border border-success-200 bg-white px-4 text-sm font-semibold text-success-700 hover:bg-success-50" data-id="element-108" data-mp="p2bstboxx4q9">
              Undo
            </button>
          </div> : <>
            <p className="text-xl font-bold text-navy-900" data-id="element-109" data-mp="30s5q925hvzah">{upNext.action}</p>
            <p className="mt-2 text-sm leading-relaxed text-navy-600" data-id="element-110" data-mp="2vuci43kmsv7k">
              {upNext.reason}
            </p>

            <dl className="mt-4 space-y-2 rounded-xl bg-navy-50/70 p-4 text-sm" data-id="element-111" data-mp="nl4p1vu6t1qx">
              <div className="flex flex-wrap justify-between gap-2" data-id="element-112" data-mp="sequp41xo5d8">
                <dt className="font-medium text-navy-500" data-id="element-113" data-mp="emkfplaio4fd">Opportunity</dt>
                <dd className="text-right font-semibold text-navy-900" data-id="element-114" data-mp="incqwk2y8jea">
                  {upNext.detail}
                </dd>
              </div>
              <div className="flex flex-wrap justify-between gap-2" data-id="element-115" data-mp="16e8nduico2rv">
                <dt className="font-medium text-navy-500" data-id="element-116" data-mp="1bcjwu44qgb9k">Source</dt>
                <dd className="text-right text-navy-700" data-id="element-117" data-mp="1b9nzagwsroln">{upNext.source}</dd>
              </div>
              <div className="flex flex-wrap justify-between gap-2" data-id="element-118" data-mp="xell76hsqsxr">
                <dt className="font-medium text-navy-500" data-id="element-119" data-mp="129sinwkh31e8">Phone</dt>
                <dd className="text-right text-navy-700" data-id="element-120" data-mp="3i28b72t32rnb">{upNext.phone}</dd>
              </div>
            </dl>

            <p className="mt-3 flex items-start gap-1.5 text-xs font-medium text-danger-700" data-id="element-121" data-mp="3n2m7jnlbod5i">
              <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-122" data-mp="3ejiy6axbtsjc" />
              {upNext.slaLabel}
            </p>

            {deferredLabel ? <p className="mt-3 rounded-lg bg-warn-50 px-3 py-2 text-xs font-medium text-warn-700 ring-1 ring-warn-200" data-id="element-123" data-mp="3oiq96rgl9b3a">
                Deferred — {deferredLabel}
              </p> : null}

            <div className="mt-5 grid grid-cols-2 gap-2" data-id="element-124" data-mp="2vtt5btd4jgu2">
              <button type="button" onClick={() => setDialog('call')} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-125" data-mp="2vucbydxbqiss">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-126" data-mp="30sdu8wmxee37" />
                Call
              </button>
              <button type="button" onClick={() => setDialog('email')} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-800 hover:bg-navy-50" data-id="element-127" data-mp="2d20b5vmbjpkr">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="gz5bo93tdqj2" />
                Email
              </button>
            </div>

            {/* Desktop / tablet: secondary actions inline */}
            <div className="mt-2 hidden gap-2 sm:grid sm:grid-cols-3" data-id="element-129" data-mp="prp42rtiiyvg">
              <button type="button" onClick={() => onViewRecord('new-leads')} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-130" data-mp="3p8f0q0gurqwy">
                <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-131" data-mp="72s88jigvyb4" />
                View record
              </button>
              <button type="button" onClick={() => setDialog('defer')} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-132" data-mp="1dhb4filmfk51">
                <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-133" data-mp="1nnaxtutdgy6x" />
                Defer
              </button>
              <button type="button" onClick={handleComplete} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border border-success-200 bg-success-50 px-3 text-sm font-semibold text-success-700 hover:bg-success-100" data-id="element-134" data-mp="1iu8c28uu6t1a">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="14u71g1ors78m" />
                Complete
              </button>
            </div>

            {/* Mobile: overflow menu */}
            <div className="relative mt-2 sm:hidden" ref={menuRef} data-id="element-136" data-mp="vvvafyjzr7fn">
              <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-137" data-mp="2uaq29r7ubzfh">
                <MoreHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-138" data-mp="3dland04eamhv" />
                More actions
              </button>
              {menuOpen ? <div role="menu" aria-label="More actions" className="absolute bottom-12 left-0 z-10 w-full overflow-hidden rounded-xl border border-navy-100 bg-white shadow-raised" data-id="element-139" data-mp="34puy6wel4vim">
                  <button role="menuitem" type="button" onClick={() => {
              setMenuOpen(false);
              onViewRecord('new-leads');
            }} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-navy-800 hover:bg-navy-50" data-id="element-140" data-mp="2gtguntksrzw9">
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-141" data-mp="u4m15iax90wd" />
                    View record
                  </button>
                  <button role="menuitem" type="button" onClick={() => {
              setMenuOpen(false);
              setDialog('defer');
            }} className="flex w-full items-center gap-2 border-t border-navy-100 px-4 py-3 text-left text-sm font-medium text-navy-800 hover:bg-navy-50" data-id="element-142" data-mp="p6u12104i8h3">
                    <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-143" data-mp="17vqvq5zfn2zu" />
                    Defer
                  </button>
                  <button role="menuitem" type="button" onClick={handleComplete} className="flex w-full items-center gap-2 border-t border-navy-100 px-4 py-3 text-left text-sm font-medium text-success-700 hover:bg-success-50" data-id="element-144" data-mp="1ltp6l1ejohal">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-145" data-mp="cuja8r26fcsg" />
                    Mark complete
                  </button>
                </div> : null}
            </div>
          </>}
      </div>

      <Modal open={dialog === 'call'} onClose={() => setDialog(null)} title={`Call ${upNext.contactName}`} description={`${upNext.phone} · ${upNext.detail}`} footer={<>
            <button type="button" onClick={() => setDialog(null)} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-147" data-mp="wh7is6iy1k8v">
              Cancel
            </button>
            <SubmitButton saving={saving} label="Start call & log activity" onClick={() => runAction('Call logged for Morgan Taylor · 1 conversation added.')} data-id="element-148" data-mp="hr0a2iiqnfld" />
          </>} data-id="element-146" data-mp="3uedrbyityt2">
        <p className="text-sm text-navy-600" data-id="element-149" data-mp="1huhic54c5xmo">
          Suggested opener: confirm the property they’re targeting, budget
          comfort, and whether they’re working with a Realtor yet.
        </p>
        <label htmlFor="call-notes" className="mt-4 block text-sm font-semibold text-navy-800" data-id="element-150" data-mp="1rpj915e9f7rd">
          Call notes
        </label>
        <textarea id="call-notes" rows={4} defaultValue="Introduced myself, confirmed $485K purchase target in Denver. Next step: send pre-approval application." className="mt-1.5 w-full rounded-lg border border-navy-200 p-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-151" data-mp="20sc1hbz6hpis" />
      </Modal>

      <Modal open={dialog === 'email'} onClose={() => setDialog(null)} title={`Email ${upNext.contactName}`} description={upNext.email} footer={<>
            <button type="button" onClick={() => setDialog(null)} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-153" data-mp="pdod51z5xyaw">
              Cancel
            </button>
            <SubmitButton saving={saving} label="Send email" onClick={() => runAction('Email sent to Morgan Taylor and logged to the lead.')} data-id="element-154" data-mp="tdf3j4ks2cew" />
          </>} data-id="element-152" data-mp="2kgt5rbp0swsf">
        <label htmlFor="email-subject" className="block text-sm font-semibold text-navy-800" data-id="element-155" data-mp="1h3j3n1yrbu15">
          Subject
        </label>
        <input id="email-subject" type="text" defaultValue="Your Denver home financing options" className="mt-1.5 h-11 w-full rounded-lg border border-navy-200 px-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-156" data-mp="1d2z5pw6d0mdy" />
        <label htmlFor="email-body" className="mt-4 block text-sm font-semibold text-navy-800" data-id="element-157" data-mp="34rzxw7kxa85f">
          Message
        </label>
        <textarea id="email-body" rows={5} defaultValue={`Hi Morgan,\n\nThanks for reaching out about financing a $485,000 purchase in Denver. I can have a pre-approval letter ready within one business day.\n\nWhat time works for a quick 10-minute call today?\n\nJordan Reyes\nSummit Home Lending · NMLS #1184629`} className="mt-1.5 w-full rounded-lg border border-navy-200 p-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-158" data-mp="2uz19appfehlt" />
      </Modal>

      <Modal open={dialog === 'defer'} onClose={() => setDialog(null)} title="Defer this recommendation" description="It will return to Up Next at the time you choose." footer={<>
            <button type="button" onClick={() => setDialog(null)} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-160" data-mp="2bccn5jt5apf0">
              Cancel
            </button>
            <SubmitButton saving={saving} label="Defer" onClick={() => {
        const option = deferOptions.find(o => o.id === deferChoice);
        runAction(`Deferred — returning ${option?.label.toLowerCase()}.`, () => setDeferredLabel(option?.detail ?? null));
      }} data-id="element-161" data-mp="1xb0bhkl0p9yx" />
          </>} data-id="element-159" data-mp="2h1w6cyl0dyus">
        <fieldset data-id="element-162" data-mp="1dlzmxxi1z283">
          <legend className="text-sm font-semibold text-navy-800" data-id="element-163" data-mp="19mhdpyzlgfl3">
            Remind me
          </legend>
          <div className="mt-2 space-y-2" data-id="element-164" data-mp="14nml65btrcns">
            {deferOptions.map(option => <label key={option.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${deferChoice === option.id ? 'border-brand-400 bg-brand-50/60' : 'border-navy-200 hover:bg-navy-50'}`} data-id="element-165" data-mp="3gaq4zkg0wdh6">
                <input type="radio" name="defer-option" value={option.id} checked={deferChoice === option.id} onChange={() => setDeferChoice(option.id)} className="mt-0.5 h-4 w-4 text-brand-600" data-id="element-166" data-mp="3u9g0wwr3w73y" />
                <span data-id="element-167" data-mp="36jdd4su85nb5">
                  <span className="block text-sm font-semibold text-navy-900" data-id="element-168" data-mp="36itvzecef2y6">
                    {option.label}
                  </span>
                  <span className="block text-xs text-navy-500" data-id="element-169" data-mp="2dwsi9iejit2u">
                    {option.detail}
                  </span>
                </span>
              </label>)}
          </div>
        </fieldset>
        <p className="mt-4 rounded-lg bg-warn-50 px-3 py-2 text-xs font-medium text-warn-700 ring-1 ring-warn-200" data-id="element-170" data-mp="3m57y6vrc8o0s">
          Deferring a never-contacted lead may push it outside the 5-minute
          contact SLA.
        </p>
      </Modal>
    </section>;
}
function SubmitButton({
  saving,
  label,
  onClick
}: {
  saving: boolean;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} disabled={saving} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-70" data-id="element-171" data-mp="336lxvj3nb0xx">
      {saving ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-172" data-mp="3798ppk0bglv4" /> : null}
      {saving ? 'Saving…' : label}
    </button>;
}